import {
  QuestionMarkCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { motion } from "motion/react";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function Faq() {
  const intl = useIntl();
  const faqs: Array<[string, string, any]> = useMemo(
    () =>
      [
        [
          "faq_4_title",
          "faq_4_text",
          {
            join: (
              <Link
                className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 underline transition-colors duration-200"
                to="/join"
              >
                {intl.formatMessage({ id: "here", defaultMessage: "here" })}
              </Link>
            ),
          },
        ],
        [
          "faq_6_title",
          "faq_6_text",
          {
            pricing: (
              <Link
                className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 underline transition-colors duration-200"
                to="/pages/pricing"
              >
                {intl.formatMessage({
                  id: "pricing_title",
                  defaultMessage: "Pricing",
                })}
              </Link>
            ),
          },
        ],
        ["faq_5_title", "faq_5_text"],
        ["faq_1_title", "faq_1_text"],
        ["faq_2_title", "faq_2_text"],
        ["faq_3_title", "faq_3_text"],
      ] as any,
    [intl.locale]
  );

  return (
    <motion.div
      className="rounded-2xl bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 dark:from-gray-800 dark:via-gray-800 dark:to-gray-700 p-12 lg:p-16 shadow-lg border border-slate-200/60 dark:border-gray-600/60"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
    >
      <div className="text-center space-y-6 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
            <SparklesIcon className="w-3 h-3 mr-1.5" />
            Get answers
          </span>
        </motion.div>

        <motion.h3
          className="text-3xl sm:text-4xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight flex flex-row items-center justify-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <motion.div
            whileHover={{ rotate: 15, scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
          >
            <QuestionMarkCircleIcon className="w-8 h-8 text-slate-600 dark:text-slate-400" />
          </motion.div>
          {intl.formatMessage({ id: "faq", defaultMessage: "FAQs" })}
        </motion.h3>

        <motion.p
          className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Everything you need to know about getting started with our restaurant
          platform.
        </motion.p>
      </div>

      <motion.div
        className="w-full max-w-4xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        viewport={{ once: true }}
      >
        <Accordion type="single" className="space-y-4">
          {faqs.map(([title, text, values], index) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <AccordionItem
                value={title}
                className="group w-full bg-white dark:bg-gray-800 overflow-hidden rounded-xl shadow-sm hover:shadow-lg duration-300 border border-gray-200/60 dark:border-gray-700/60 hover:border-gray-300/60 dark:hover:border-gray-600/60"
              >
                <AccordionTrigger className="relative flex w-full items-center justify-between px-6 py-6 text-left text-lg font-semibold focus:outline-none focus-visible:ring focus-visible:ring-slate-500/20 transition-colors duration-200">
                  <span className="font-medium text-left text-gray-900 cursor-pointer dark:text-gray-100 group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors duration-200 pr-4">
                    {intl.formatMessage({ id: title })}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    {intl.formatMessage({ id: text }, values)}
                  </motion.div>
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      </motion.div>
    </motion.div>
  );
}
